import {
  ArrowRightOutlined,
  FileAddOutlined,
  RetweetOutlined,
} from '@ant-design/icons';
import { Button, Card, Divider, Flex, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Show from 'components/Show';
import TaskInfo from 'components/TaskInfo';
import TaskDetailsSkeleton from './TaskDetailsSkeleton';
import TaskFailureNotesForm from './TaskFailureNotesForm';
import TaskReschedulerForm from './TaskReschedulerForm';
import TimeWindowCard from './TimeWindowCard';

import { usePermissions } from 'context/PermissionsContext';
import type { TaskDetail } from 'screens/task-management/types';
import StatusTag from 'components/StatusTag';
import { mapStatus } from 'screens/task-management/util';

interface TaskDetailsTabContentProps {
  isLoading: boolean;
  task: TaskDetail;
}

const statusWithRescheduleDisabled = new Set(['INCOMPLETE', 'COMPLETED']);

const TaskDetailsTabContent = ({
  isLoading,
  task,
}: TaskDetailsTabContentProps) => {
  const { t } = useTranslation();
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [isAddingFailureNote, setIsAddingFailureNote] = useState(false);
  const { checkPermissions: hasPermissions } = usePermissions();

  // As of now, redirect is only implemented for these two task types --- IGNORE ---
  const shouldShowGoToTaskLink =
    task?.taskDescription?.startsWith('Pipe Installation') ||
    task?.taskDescription?.startsWith('Drying Event');

  const shouldShowRescheduleButton =
    hasPermissions('task:schedule') &&
    !statusWithRescheduleDisabled.has(task?.status) &&
    !isRescheduling;

  const shouldShowAddFailureNoteButton =
    task?.status === 'INCOMPLETE' && !isAddingFailureNote;

  if (isLoading) {
    return <TaskDetailsSkeleton />;
  }

  return (
    <Flex vertical gap={16} align="flex-start">
      <Card>
        <Flex vertical gap={16}>
          <span>
            <StatusTag
              status={mapStatus(task?.status)}
              style={{ padding: '2px 13px', marginLeft: -4 }}
            />
          </span>

          <TimeWindowCard
            status={task?.status}
            startDate={task?.startDate}
            endDate={task?.endDate}
            plannedDate={task?.plannedDate}
          />

          <Divider
            className="m-0"
            style={{ background: '#0000001A', opacity: 0.6 }}
          />

          <Flex wrap="wrap" className="row-wrap-container">
            <TaskInfo
              header={t('taskManagement.labels.agronomist')}
              value={task?.agronomistName}
            />
            <TaskInfo
              header={t('taskManagement.labels.district')}
              value={task?.district}
            />
            <TaskInfo
              header={t('taskManagement.labels.plantingGroup')}
              value={task?.plantingGroupName}
            />
            <TaskInfo
              header={t('taskManagement.labels.farmers')}
              value={task?.farmerCount || 0}
            />

            <TaskInfo
              header={t('taskManagement.labels.farmerGroup')}
              value={task?.farmerGroupName}
            />

            <TaskInfo
              header={t('taskManagement.labels.rescheduleCount')}
              value={task?.rescheduleCount || 0}
            />
          </Flex>

          {task?.failureNotes?.map((item, index) => (
            <TaskInfo
              key={index}
              header={
                t('taskManagement.labels.failureNotes') +
                (index > 0 ? ` [${index + 1}]` : '')
              }
              renderValue={() => (
                <Typography.Text
                  style={{
                    whiteSpace: 'break-spaces',
                    wordBreak: 'break-word',
                  }}
                >
                  {item?.note}
                </Typography.Text>
              )}
            />
          ))}

          <Show when={shouldShowGoToTaskLink}>
            <Link
              to={`/awd-progress?seasonId=${task?.plantingGroupId}&taskType=${task?.taskDescription}`}
              className="d-flex align-items-center text-primary mt-2"
            >
              <span>{t('taskManagement.labels.goToTask')}</span>
              <ArrowRightOutlined className="ms-2" />
            </Link>
          </Show>
        </Flex>
      </Card>
      <Show when={shouldShowRescheduleButton}>
        <Button
          type="primary"
          className="w-fit"
          icon={<RetweetOutlined />}
          onClick={() => setIsRescheduling(true)}
        >
          {' '}
          {t('taskManagement.labels.rescheduleTask')}
        </Button>
      </Show>
      <Show when={shouldShowAddFailureNoteButton}>
        <Button
          type="primary"
          icon={<FileAddOutlined />}
          className="w-fit"
          onClick={() => setIsAddingFailureNote(true)}
        >
          {' '}
          {t('taskManagement.labels.addFailureNote')}
        </Button>
      </Show>

      <Show when={isRescheduling}>
        <TaskReschedulerForm
          task={task}
          onCancel={() => setIsRescheduling(false)}
          onRescheduleSuccess={() => setIsRescheduling(false)}
        />
      </Show>

      <Show when={isAddingFailureNote}>
        <TaskFailureNotesForm
          taskId={task?.taskId || ''}
          onCancel={() => setIsAddingFailureNote(false)}
          onAddFailureNotesSuccess={() => setIsAddingFailureNote(false)}
        />
      </Show>
    </Flex>
  );
};

export default TaskDetailsTabContent;
